import React, { useCallback, useEffect, useState } from 'react';
import { measuresAPI } from '../../services/api';

const MEASURE_TYPES = ['count', 'sum', 'avg', 'min', 'max', 'count_distinct', 'number'];
const EMPTY_MEASURE = { name: '', title: '', type: 'count', column: '', sql: '', filters: '', format: '' };

/** Inline editor for a dataset's custom measures (measure DSL). */
const MeasuresEditor = ({ datasetName, companyId, columns, isAdmin, onChanged }) => {
  const [measures, setMeasures] = useState([]);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    try {
      const res = await measuresAPI.list(datasetName, companyId);
      setMeasures(res?.success ? res.data : []);
    } catch (err) {
      setError(describeError(err, 'Failed to load measures'));
    }
  }, [datasetName, companyId]);

  useEffect(() => { load(); }, [load]);

  const payload = () => ({
    name: form.name.trim(),
    title: form.title.trim() || form.name.trim(),
    type: form.type,
    ...(form.column ? { column: form.column } : {}),
    ...(form.sql.trim() ? { sql: form.sql.trim() } : {}),
    ...(form.filters.trim() ? { filters: form.filters.split('\n').map((f) => f.trim()).filter(Boolean) } : {}),
    ...(form.format ? { format: form.format } : {}),
  });

  const validate = async () => {
    try {
      setError('');
      const res = await measuresAPI.validate(datasetName, companyId, payload());
      setPreview(res?.success ? res.data.compiled : null);
    } catch (err) {
      setPreview(null);
      setError(describeError(err, 'Validation failed'));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const res = form.id
        ? await measuresAPI.update(datasetName, companyId, form.id, payload())
        : await measuresAPI.create(datasetName, companyId, payload());
      if (!res?.success) throw new Error(res?.error || 'Save failed');
      setForm(null);
      setPreview(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(describeError(err, 'Failed to save measure'));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete measure "${m.name}"? Reports and KPIs using it will break.`)) return;
    try {
      await measuresAPI.delete(datasetName, companyId, m.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(describeError(err, 'Failed to delete measure'));
    }
  };

  const edit = (m) => setForm({
    id: m.id, name: m.name, title: m.title, type: m.type, column: m.column || '', sql: m.sql || '',
    filters: (m.filters || []).join('\n'), format: m.format || '',
  });

  const needsColumn = ['sum', 'avg', 'min', 'max', 'count_distinct'].includes(form?.type);

  return (
    <section className="measures-editor">
      <h4>Custom measures</h4>
      {error && <div className="error-message">{error}</div>}
      {measures.length === 0 && !form && <p className="dataset-empty">None yet. Measures defined here appear in the report builder and can be tracked as KPIs.</p>}
      {measures.length > 0 && (
        <ul className="dataset-columns">
          {measures.map((m) => (
            <li key={m.id}>
              <code>{m.name}</code>
              <span className="dataset-type">{m.type}{m.column ? ` of ${m.column}` : ''}{m.filters?.length ? ` · ${m.filters.length} filter${m.filters.length === 1 ? '' : 's'}` : ''}</span>
              <span>{m.title}</span>
              {isAdmin && (
                <span className="dataset-actions" style={{ marginLeft: 'auto' }}>
                  <button className="btn-secondary btn-small" onClick={() => edit(m)}>Edit</button>
                  <button className="btn-danger btn-small" onClick={() => remove(m)}>Delete</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {isAdmin && !form && (
        <button className="btn-secondary btn-small" onClick={() => setForm({ ...EMPTY_MEASURE })} style={{ marginTop: '0.5rem' }}>+ Add measure</button>
      )}
      {form && (
        <form className="measure-form" onSubmit={save}>
          <div className="measure-form-row">
            <label>Name <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="billable_hours" required /></label>
            <label>Title <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Billable hours" /></label>
            <label>Type
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {MEASURE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            {needsColumn && (
              <label>Column
                <select value={form.column} onChange={(e) => setForm({ ...form, column: e.target.value })}>
                  <option value="">— choose —</option>
                  {columns.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
                </select>
              </label>
            )}
            <label>Format
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
                <option value="">default</option>
                <option value="percent">percent</option>
                <option value="currency">currency</option>
                <option value="number">number</option>
              </select>
            </label>
          </div>
          {(form.type === 'number' || (!needsColumn && form.type !== 'count') || form.sql) && (
            <label className="measure-form-wide">SQL expression
              <input value={form.sql} onChange={(e) => setForm({ ...form, sql: e.target.value })} placeholder="{measures.billable_hours} / NULLIF({measures.hours_sum}, 0)" />
              <small>Use {'{column}'} for columns and {'{measures.name}'} for other measures.</small>
            </label>
          )}
          <label className="measure-form-wide">Filters (one per line)
            <textarea rows={2} value={form.filters} onChange={(e) => setForm({ ...form, filters: e.target.value })} placeholder={"{billable} = true\n{work_type} = 'remedial'"} />
          </label>
          {preview && (
            <div className="measure-preview">
              <strong>Compiles to:</strong> <code>{preview.type}{preview.sql ? `(${preview.sql})` : ''}</code>
              {preview.filters?.length > 0 && <div>where <code>{preview.filters.map((f) => f.sql).join(' AND ')}</code></div>}
            </div>
          )}
          <div className="dataset-actions">
            <button type="button" className="btn-secondary btn-small" onClick={validate}>Validate</button>
            <button type="submit" className="btn-primary btn-small">{form.id ? 'Save changes' : 'Create measure'}</button>
            <button type="button" className="btn-secondary btn-small" onClick={() => { setForm(null); setPreview(null); }}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
};

export default MeasuresEditor;
